
<template>
  <div class="deepseek-demo-page">
    <div class="page-layout">
      <transition name="config-panel">
        <div v-show="!configPanelCollapsed" class="config-panel">
          <el-card shadow="never" class="panel-card">
            <div slot="header" class="card-header">
              <div class="header-left">
                <span>DeepSeek 问答配置</span>
              </div>
              <el-button type="text" icon="el-icon-arrow-left" @click="toggleConfigPanel">
                收起
              </el-button>
            </div>
            <div class="card-body-scroll">
              <el-form label-position="top" @submit.native.prevent>
                <el-form-item label="API Key">
                  <el-input v-model.trim="apiKey" type="password" show-password placeholder="请输入 DeepSeek API Key" @input="persistConfig" />
                </el-form-item>
                <el-form-item label="模型">
                  <el-select v-model="model" style="width: 100%" @change="persistConfig">
                    <el-option label="deepseek-chat" value="deepseek-chat" />
                    <el-option label="deepseek-reasoner" value="deepseek-reasoner" />
                  </el-select>
                </el-form-item>
                <el-form-item label="系统提示词">
                  <el-input v-model.trim="systemPrompt" type="textarea" :rows="6" maxlength="500" show-word-limit placeholder="可选，用来约束回答风格和范围" @input="persistConfig" />
                </el-form-item>
              </el-form>
              <div class="config-tip">
                <p>1. 当前版本直接从浏览器请求 DeepSeek 接口。</p>
                <p>2. API Key 仅保存在当前浏览器本地。</p>
                <p>3. 生产环境建议改成后端代理调用。</p>
              </div>
            </div>
          </el-card>
        </div>
      </transition>

      <div class="workspace-panel">
        <el-card shadow="never" class="panel-card">
          <div slot="header" class="card-header">
            <div class="header-left">
              <span>DeepSeek 问答</span>
              <el-button type="text" icon="el-icon-s-operation" @click="toggleConfigPanel">
                {{ configPanelCollapsed ? '展开配置' : '收起配置' }}
              </el-button>
            </div>
            <div class="header-actions">
              <el-button size="mini" @click="clearAll" :disabled="loading">清空</el-button>
              <el-button v-if="loading" size="mini" type="danger" plain @click="cancelRequest">停止生成</el-button>
            </div>
          </div>

          <div class="workspace-body">
            <div class="input-column">
              <div class="section-title">输入你的问题</div>
              <div class="input-meta">
                <span>支持普通问答、代码生成、Markdown 输出</span>
                <span>Ctrl + Enter 可直接提交</span>
              </div>
              <div class="question-editor">
                <el-input v-model.trim="question" class="question-textarea" type="textarea" resize="none" maxlength="1000" show-word-limit placeholder="例如：请帮我写一个 Vue2 + Element UI 的输入框示例，或者给我一个 ECharts 饼图配置。" @keydown.ctrl.enter.native="submitQuestion" />
              </div>
              <div class="question-actions">
                <el-button @click="copyText(question, '问题内容为空')" :disabled="!question">复制问题</el-button>
                <el-button type="primary" :loading="loading" @click="submitQuestion">{{ loading ? '生成中...' : '开始提问' }}</el-button>
              </div>
            </div>

            <div class="output-column">
              <div v-if="reasoning" class="reasoning-section">
                <div class="section-title">思考过程</div>
                <div class="output-panel markdown-content" v-html="renderMarkdownContent(reasoning)"></div>
              </div>

              <div class="answer-section">
                <div class="section-header">
                  <div class="section-title">回答结果</div>
                  <el-button type="text" @click="copyText(answer, '回答内容为空')" :disabled="!answer">复制回答</el-button>
                </div>

                <div class="result-layout" :class="{ 'has-preview': previewEligible }">
                  <div class="result-pane text-pane">
                    <div v-if="answer" class="output-panel markdown-content" v-html="renderMarkdownContent(answer)"></div>
                    <div v-else class="output-panel is-placeholder">这里会展示 DeepSeek 返回的回答内容。</div>
                  </div>

                  <div v-if="previewEligible" class="result-pane preview-pane">
                    <div class="preview-header">
                      <span class="section-title preview-title">页面预览</span>
                      <div class="preview-header-actions">
                        <span v-if="previewTypeLabel" class="preview-tag">{{ previewTypeLabel }}</span>
                        <el-radio-group v-if="hasPreviewSource" :value="previewViewMode" size="mini" @input="switchPreviewView">
                          <el-radio-button label="preview">预览</el-radio-button>
                          <el-radio-button label="source">源码</el-radio-button>
                        </el-radio-group>
                      </div>
                    </div>
                    <div class="preview-surface">
                      <div v-if="previewViewMode === 'preview' && previewMode === 'echarts'" ref="chartPreview" class="chart-preview"></div>
                      <iframe v-else-if="previewViewMode === 'preview' && previewMode === 'component'" class="preview-frame" :srcdoc="previewDocument" sandbox="allow-scripts allow-forms" title="前端代码预览"></iframe>
                      <div v-else-if="previewViewMode === 'source' && hasPreviewSource" class="output-panel markdown-content preview-source-panel" v-html="renderPreviewSource()"></div>
                      <div v-else class="preview-placeholder">{{ previewMessage }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>
            </div>
          </div>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'

const API_URL = 'https://api.deepseek.com/chat/completions'
const STORAGE_KEY = 'deepseek_demo_config'
const FRONTEND_PATTERN = /(前端|页面|组件|界面|布局|样式|css|html|vue|element|el-|echarts|图表|饼图|柱状图|折线图|输入框|按钮|表格|表单|弹窗|对话框|卡片|下拉|选择器)/i
const TEMPLATE_PATTERN = /<(template|div|section|main|article|el-[a-z-]+)/i

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function renderCodeBlock(content, language) {
  const normalizedLanguage = String(language || '').trim()
  const label = normalizedLanguage ? `<div class="code-block__label">${escapeHtml(normalizedLanguage)}</div>` : ''
  const languageClass = normalizedLanguage ? ` class="language-${normalizedLanguage}"` : ''
  return '<div class="code-block">' + label + `<pre><code${languageClass}>${escapeHtml(content)}</code></pre>` + '</div>'
}

function looksLikeCodeLine(line) {
  const text = String(line || '').trim()
  if (!text) return false
  return /^<\/?[a-zA-Z][^>]*>$/.test(text) ||
    /^(const|let|var|function|class|import|export|return|if|else|for|while|switch|case|try|catch)\b/.test(text) ||
    /^(data|methods|computed|watch|props|components)\s*\(/.test(text) ||
    /^[\w$-]+\s*:\s*.+[,]{0,1}$/.test(text) ||
    /^(<template>|<script>|<style)/.test(text) ||
    /^(<\/template>|<\/script>|<\/style>)$/.test(text) ||
    /(=>|\{\s*$|^\}|\)\s*\{|<\/|\/>|v-model=|v-if=|v-for=|@click=|:\w+=)/.test(text)
}

function renderInlineMarkdown(text) {
  let html = escapeHtml(text)
  html = html.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>')
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/\*([^*\n]+)\*/g, '<em>$1</em>')
  return html
}

function renderMarkdown(text) {
  const source = String(text || '').replace(/\r\n/g, '\n').trim()
  if (!source) return ''
  const codeBlocks = []
  const normalized = source.replace(/```([a-zA-Z0-9_-]+)?\n?([\s\S]*?)(?:```|$)/g, (match, language, code) => {
    const token = `__CODE_BLOCK_${codeBlocks.length}__`
    codeBlocks.push(renderCodeBlock(String(code || '').replace(/\n+$/, ''), language))
    return token
  })

  const html = normalized.split(/\n{2,}/).map(item => item.trim()).filter(Boolean).map(block => {
    if (/^__CODE_BLOCK_\d+__$/.test(block)) return codeBlocks[Number(block.match(/\d+/)[0])]
    if (/^#{1,6}\s/.test(block)) {
      const match = block.match(/^(#{1,6})\s+([\s\S]+)$/)
      const level = match[1].length
      return `<h${level}>${renderInlineMarkdown(match[2].trim())}</h${level}>`
    }
    const rawLines = block.split('\n')
    const lines = rawLines.map(item => item.trim()).filter(Boolean)
    if (lines.length && lines.every(line => /^[-*+]\s+/.test(line))) {
      return `<ul>${lines.map(line => `<li>${renderInlineMarkdown(line.replace(/^[-*+]\s+/, ''))}</li>`).join('')}</ul>`
    }
    if (lines.length && lines.every(line => /^\d+\.\s+/.test(line))) {
      return `<ol>${lines.map(line => `<li>${renderInlineMarkdown(line.replace(/^\d+\.\s+/, ''))}</li>`).join('')}</ol>`
    }
    const nonEmptyCount = rawLines.filter(Boolean).length
    const codeLineCount = rawLines.filter(line => looksLikeCodeLine(line)).length
    if (nonEmptyCount >= 3 && codeLineCount >= Math.max(2, Math.ceil(nonEmptyCount * 0.45))) {
      return renderCodeBlock(rawLines.join('\n').replace(/^\n+|\n+$/g, ''), '')
    }
    return `<p>${lines.map(line => renderInlineMarkdown(line)).join('<br>')}</p>`
  }).join('')

  return html.replace(/__CODE_BLOCK_(\d+)__/g, (match, index) => codeBlocks[Number(index)] || '')
}

export default {
  name: 'DeepSeekDemo',
  data() {
    return {
      apiKey: '',
      model: 'deepseek-chat',
      systemPrompt: '你是一个专业、清晰、可靠的中文 AI 助手。请基于用户问题给出准确、结构清楚的回答。',
      question: '',
      answer: '',
      reasoning: '',
      loading: false,
      errorMessage: '',
      abortController: null,
      configPanelCollapsed: true,
      previewMode: '',
      previewViewMode: 'preview',
      previewMessage: '前端类问题会在这里显示代码生成的页面预览。',
      previewDocument: '',
      previewChartOption: null,
      previewDetectedFromAnswer: false,
      previewSourceCode: '',
      previewSourceLanguage: '',
      chartInstance: null
    }
  },
  computed: {
    previewEligible() {
      return this.isFrontendQuestion(this.question) || this.previewDetectedFromAnswer
    },
    previewTypeLabel() {
      if (this.previewMode === 'echarts') return 'ECharts'
      if (this.previewMode === 'component') return '组件预览'
      return ''
    },
    hasPreviewSource() {
      return Boolean(this.previewSourceCode)
    }
  },
  watch: {
    configPanelCollapsed() {
      this.$nextTick(() => {
        this.resizeChart()
      })
    }
  },
  created() {
    this.restoreConfig()
  },
  mounted() {
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    this.cancelRequest()
    this.disposeChart()
    window.removeEventListener('resize', this.resizeChart)
  },
  methods: {
    restoreConfig() {
      try {
        const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null')
        if (!parsed) return
        this.apiKey = parsed.apiKey || ''
        this.model = parsed.model || 'deepseek-chat'
        this.systemPrompt = parsed.systemPrompt || this.systemPrompt
      } catch (error) {
        console.warn('Failed to restore DeepSeek config:', error)
      }
    },
    persistConfig() {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          apiKey: this.apiKey,
          model: this.model,
          systemPrompt: this.systemPrompt
        })
      )
    },
    toggleConfigPanel() {
      this.configPanelCollapsed = !this.configPanelCollapsed
    },
    switchPreviewView(mode) {
      this.previewViewMode = mode
      if (this.previewMode === 'echarts' && mode !== 'preview') {
        this.disposeChart()
        return
      }
      if (mode === 'preview') {
        this.$nextTick(() => {
          this.renderChartPreview()
        })
      }
    },
    isFrontendQuestion(text) {
      return FRONTEND_PATTERN.test(String(text || ''))
    },
    async submitQuestion() {
      if (this.loading) return
      if (!this.apiKey) return this.$toast.warning('请先输入 DeepSeek API Key')
      if (!this.question) return this.$toast.warning('请输入你的问题')

      this.persistConfig()
      this.answer = ''
      this.reasoning = ''
      this.errorMessage = ''
      this.loading = true
      this.abortController = new AbortController()
      this.resetPreviewState()

      try {
        const response = await fetch(API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.apiKey}`
          },
          body: JSON.stringify({
            model: this.model,
            stream: true,
            messages: [
              { role: 'system', content: this.systemPrompt || '你是一个专业、清晰、可靠的中文 AI 助手。' },
              { role: 'user', content: this.question }
            ]
          }),
          signal: this.abortController.signal
        })

        if (!response.ok) {
          const errorText = await response.text()
          throw new Error(this.parseErrorMessage(errorText) || `请求失败：${response.status}`)
        }
        if (!response.body || !window.TextDecoder) {
          throw new Error('当前浏览器不支持流式响应读取')
        }

        const reader = response.body.getReader()
        const decoder = new TextDecoder('utf-8')
        let buffer = ''

        while (true) {
          const { value, done } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''
          lines.forEach(line => this.processStreamLine(line))
        }

        if (buffer) this.processStreamLine(buffer)
        if (!this.answer && !this.reasoning) {
          this.errorMessage = '接口已返回，但没有解析到有效内容'
        } else {
          this.refreshPreview()
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          this.$toast.info('已停止生成')
        } else {
          console.error('DeepSeek request failed:', error)
          this.errorMessage = error.message || '请求失败，请稍后重试'
          this.$toast.error(this.errorMessage)
        }
      } finally {
        this.loading = false
        this.abortController = null
      }
    },
    processStreamLine(line) {
      const trimmedLine = String(line || '').trim()
      if (!trimmedLine || !trimmedLine.startsWith('data:')) return
      const payload = trimmedLine.replace(/^data:\s*/, '')
      if (payload === '[DONE]') return
      try {
        const parsed = JSON.parse(payload)
        const delta = parsed.choices && parsed.choices[0] && parsed.choices[0].delta
        if (!delta) return
        if (delta.reasoning_content) this.reasoning += delta.reasoning_content
        if (delta.content) this.answer += delta.content
      } catch (error) {
        console.warn('Failed to parse stream line:', trimmedLine, error)
      }
    },
    parseErrorMessage(text) {
      try {
        const parsed = JSON.parse(text)
        return parsed.error && parsed.error.message
      } catch (error) {
        return text
      }
    },
    cancelRequest() {
      if (this.abortController) this.abortController.abort()
    },
    clearAll() {
      this.question = ''
      this.answer = ''
      this.reasoning = ''
      this.errorMessage = ''
      this.resetPreviewState()
    },
    renderMarkdownContent(text) {
      return renderMarkdown(text)
    },
    renderPreviewSource() {
      if (!this.previewSourceCode) return ''
      return renderCodeBlock(this.previewSourceCode, this.previewSourceLanguage)
    },
    async copyText(text, emptyMessage) {
      if (!text) return this.$toast.warning(emptyMessage)
      try {
        await navigator.clipboard.writeText(text)
        this.$toast.success('复制成功')
      } catch (error) {
        this.$toast.error('复制失败')
      }
    },
    resetPreviewState() {
      this.previewMode = ''
      this.previewViewMode = 'preview'
      this.previewMessage = '前端类问题会在这里显示代码生成的页面预览。'
      this.previewDocument = ''
      this.previewChartOption = null
      this.previewDetectedFromAnswer = false
      this.previewSourceCode = ''
      this.previewSourceLanguage = ''
      this.disposeChart()
    },
    refreshPreview() {
      this.resetPreviewState()
      if (!this.answer) return

      const blocks = this.extractCodeBlocks(this.answer)
      const chartPreview = this.extractEChartsPreview(blocks)
      if (chartPreview) {
        this.previewDetectedFromAnswer = true
        this.previewMode = 'echarts'
        this.previewChartOption = chartPreview.option
        this.previewSourceCode = chartPreview.sourceCode
        this.previewSourceLanguage = chartPreview.language
        this.previewMessage = ''
        this.$nextTick(() => {
          this.renderChartPreview()
        })
        return
      }

      const componentPreview = this.extractComponentPreview(blocks)
      if (componentPreview) {
        this.previewDetectedFromAnswer = true
        this.previewMode = 'component'
        this.previewDocument = this.buildPreviewDocument(componentPreview)
        this.previewSourceCode = componentPreview.sourceCode
        this.previewSourceLanguage = componentPreview.language
        this.previewMessage = ''
        return
      }

      if (this.isFrontendQuestion(this.question)) {
        this.previewMessage = '识别到这是前端类问题，但暂时没有提取到可直接预览的代码。你可以要求模型返回完整的 Vue / HTML / ECharts 代码块。'
      }
    },
    extractCodeBlocks(text) {
      const blocks = []
      const pattern = /```([a-zA-Z0-9_-]+)?\n?([\s\S]*?)(?:```|$)/g
      const source = String(text || '')
      let match = pattern.exec(source)
      while (match) {
        blocks.push({ language: String(match[1] || '').trim().toLowerCase(), code: String(match[2] || '').trim() })
        match = pattern.exec(source)
      }
      return blocks
    },
    isEChartsBlock(code) {
      return /(echarts(?:\.init)?|setOption\s*\(|series\s*:|xAxis\s*:|yAxis\s*:|legend\s*:|tooltip\s*:|pie|bar|line|radar|dataset\s*:|visualMap\s*:|grid\s*:)/i.test(String(code || ''))
    },
    extractEChartsPreview(blocks) {
      const candidates = (blocks || []).filter(block => this.isEChartsBlock(block.code))
      for (let index = 0; index < candidates.length; index += 1) {
        const option = this.tryParseEChartsOption(candidates[index].code)
        if (option && typeof option === 'object') {
          return { option, sourceCode: candidates[index].code, language: candidates[index].language || 'javascript' }
        }
      }
      return null
    },
    tryParseEChartsOption(code) {
      const source = String(code || '').trim()
      if (!source) return null
      const cleaned = source.replace(/^\s*import[^\n]*$/gm, '').trim()
      const executable = this.extractRunnableJavaScript(cleaned)
      const direct = this.tryEvaluateObject(cleaned) || this.tryEvaluateObject(executable)
      if (direct && direct.series) return direct

      const targetName = this.extractSetOptionTargetName(executable)
      const targetLiteral = targetName ? this.extractObjectLiteralAfterName(executable, targetName) : ''
      const optionLiteral = targetLiteral || this.extractObjectLiteralAfterName(executable, 'option') || this.extractInlineSetOptionObject(executable)
      const parsedOption = this.tryEvaluateObject(optionLiteral)
      if (parsedOption && parsedOption.series) return parsedOption

      try {
        const runner = new Function(
          'echarts',
          `
            let captured = null;
            const window = {};
            const document = {
              getElementById() { return {}; },
              querySelector() { return {}; },
              createElement() { return {}; }
              };
              const chart = {
                setOption(payload) { captured = payload; },
                resize() {},
                dispose() {}
              };
              const myChart = chart;
              echarts = Object.assign({}, echarts, { init() { return chart; } });
              ${executable}
              try {
                return captured || (typeof option !== 'undefined' ? option : null) || null;
              } catch (error) {
                return captured || null;
              }
          `
        )
        return runner({
          init() {
            return { setOption(payload) { return payload }, resize() {}, dispose() {} }
          },
          graphic: echarts.graphic,
          format: echarts.format,
          number: echarts.number
        })
      } catch (error) {
        return null
      }
    },
    extractRunnableJavaScript(source) {
      const text = String(source || '').trim()
      if (!text) return ''
      const scriptMatches = [...text.matchAll(/<script[^>]*>([\s\S]*?)<\/script>/gi)]
      if (scriptMatches.length) {
        return scriptMatches.map(match => String(match[1] || '').trim()).filter(Boolean).join('\n\n')
      }
      return text.replace(/^\s*```[a-zA-Z0-9_-]*\s*$/gm, '').trim()
    },
    extractSetOptionTargetName(source) {
      const argument = this.extractSetOptionArgument(source)
      if (/^[A-Za-z_$][\w$]*$/.test(argument)) return argument
      return ''
    },
    extractInlineSetOptionObject(source) {
      const argument = this.extractSetOptionArgument(source)
      if (argument && argument.trim().startsWith('{')) return argument.trim()
      return ''
    },
    extractSetOptionArgument(source) {
      const text = String(source || '')
      const match = /setOption\s*\(/.exec(text)
      if (!match) return ''

      let depth = 0
      let inString = false
      let quoteChar = ''
      let escaped = false
      const start = match.index + match[0].length

      for (let index = start; index < text.length; index += 1) {
        const current = text[index]
        if (inString) {
          if (escaped) {
            escaped = false
            continue
          }
          if (current === '\\') {
            escaped = true
            continue
          }
          if (current === quoteChar) {
            inString = false
            quoteChar = ''
          }
          continue
        }
        if (current === '"' || current === "'" || current === '`') {
          inString = true
          quoteChar = current
          continue
        }
        if (current === '(' || current === '{' || current === '[') {
          depth += 1
          continue
        }
        if (current === ',' && depth === 0) {
          return text.slice(start, index).trim()
        }
        if (current === ')' || current === '}' || current === ']') {
          if (current === ')' && depth === 0) {
            return text.slice(start, index).trim()
          }
          depth -= 1
        }
      }
      return ''
    },
    extractObjectLiteralAfterName(source, variableName) {
      const text = String(source || '')
      const target = String(variableName || '').trim()
      if (!text || !target) return ''
      const pattern = new RegExp(`${target}\\s*=\\s*\\{`, 'm')
      const matched = pattern.exec(text)
      if (!matched) return ''

      const start = matched.index + matched[0].length - 1
      let depth = 0
      let inString = false
      let quoteChar = ''
      let escaped = false

      for (let index = start; index < text.length; index += 1) {
        const current = text[index]
        if (inString) {
          if (escaped) {
            escaped = false
            continue
          }
          if (current === '\\') {
            escaped = true
            continue
          }
          if (current === quoteChar) {
            inString = false
            quoteChar = ''
          }
          continue
        }
        if (current === '"' || current === "'" || current === '`') {
          inString = true
          quoteChar = current
          continue
        }
        if (current === '{') {
          depth += 1
        } else if (current === '}') {
          depth -= 1
          if (depth === 0) return text.slice(start, index + 1)
        }
      }
      return ''
    },
    tryEvaluateObject(expression) {
      const source = String(expression || '').trim()
      if (!/^\{[\s\S]*\}$/.test(source)) return null
      try {
        return new Function(`return (${source})`)()
      } catch (error) {
        return null
      }
    },
    extractComponentPreview(blocks) {
      const sfcBlock = (blocks || []).find(block => /<(template|script|style)/i.test(block.code) && !this.isEChartsBlock(block.code))
      if (sfcBlock) {
        const template = this.extractSfcPart(sfcBlock.code, 'template')
        if (template) {
          return {
            template,
            script: this.extractSfcPart(sfcBlock.code, 'script'),
            style: this.extractSfcPart(sfcBlock.code, 'style'),
            sourceCode: sfcBlock.code,
            language: sfcBlock.language || 'vue'
          }
        }
      }

      const templateBlock = (blocks || []).find(block => TEMPLATE_PATTERN.test(block.code) && !this.isEChartsBlock(block.code))
      if (!templateBlock) return null

      const scriptBlock = (blocks || []).find(block =>
        /^(js|javascript|ts|typescript)?$/.test(block.language) &&
        /(export default|data\s*\(|methods\s*:|computed\s*:|watch\s*:)/i.test(block.code) &&
        !this.isEChartsBlock(block.code)
      )
      const styleBlock = (blocks || []).find(block => /^(css|scss|less)$/.test(block.language))

      const sourceParts = [templateBlock.code]
      if (scriptBlock) sourceParts.push('\n<script>\n' + scriptBlock.code + '\n<\/script>')
      if (styleBlock) sourceParts.push('\n<style>\n' + styleBlock.code + '\n<\/style>')

      return {
        template: this.extractSfcPart(templateBlock.code, 'template') || templateBlock.code,
        script: scriptBlock ? scriptBlock.code : '',
        style: styleBlock ? styleBlock.code : '',
        sourceCode: sourceParts.join('\n').trim(),
        language: templateBlock.language || 'vue'
      }
    },
    extractSfcPart(source, tagName) {
      const match = String(source || '').match(new RegExp(`<${tagName}[^>]*>([\\s\\S]*?)<\\/${tagName}>`, 'i'))
      return match ? match[1].trim() : ''
    },
    sanitizeComponentScript(script) {
      const source = String(script || '').replace(/^\s*import[^\n]*$/gm, '').trim()
      if (!source) return 'const __component__ = {};'
      if (/export\s+default/.test(source)) return source.replace(/export\s+default/, 'const __component__ =')
      if (/module\.exports\s*=/.test(source)) return source.replace(/module\.exports\s*=/, 'const __component__ =')
      return `const __component__ = ${source}`
    },
    buildPreviewDocument({ template, script, style }) {
      const defaultData = JSON.stringify({
        inputValue: '示例内容',
        textareaValue: '这是一段预览文本',
        selectValue: 'option-1',
        radioValue: '1',
        switchValue: true,
        checkboxValue: true,
        sliderValue: 36,
        dateValue: '',
        searchText: '',
        tableData: [
          { name: '示例一', status: '启用', value: 32 },
          { name: '示例二', status: '停用', value: 18 }
        ],
        options: [
          { label: '选项一', value: 'option-1' },
          { label: '选项二', value: 'option-2' }
        ],
        form: { name: '演示名称', desc: '这是一段预览数据', region: '北京', remark: 'Hello' }
      })

      return `<!DOCTYPE html>
<html lang="zh-CN">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/element-ui/lib/theme-chalk/index.css" />
  <style>
    html, body { margin: 0; padding: 0; height: 100%; overflow: auto; background: #f7f9fc; font-family: "Helvetica Neue", Arial, sans-serif; }
    #app { min-height: 100%; box-sizing: border-box; padding: 16px; }
    .preview-host { min-height: calc(100vh - 32px); box-sizing: border-box; }
    ${String(style || '').trim()}
  </style>
</head>
<body>
  <div id="app"></div>
  <script src="https://cdn.jsdelivr.net/npm/vue@2/dist/vue.js"><\/script>
  <script src="https://cdn.jsdelivr.net/npm/element-ui/lib/index.js"><\/script>
  <script src="https://cdn.jsdelivr.net/npm/echarts@6/dist/echarts.min.js"><\/script>
  <script>
    const __template__ = ${JSON.stringify(String(template || '').trim())};
    ${this.sanitizeComponentScript(script)}
    const __defaults__ = ${defaultData};
    const __base__ = typeof __component__ === 'object' && __component__ ? __component__ : {};
    const __userData__ = typeof __base__.data === 'function' ? __base__.data.call({}) : (__base__.data || {});
    new Vue(Object.assign({}, __base__, {
      el: '#app',
      template: '<div class="preview-host">' + __template__ + '</div>',
      data() { return Object.assign({}, __defaults__, __userData__); }
    }));
  <\/script>
</body>
</html>`
    },
    renderChartPreview() {
      if (this.previewViewMode !== 'preview' || this.previewMode !== 'echarts' || !this.previewChartOption || !this.$refs.chartPreview) return
      if (this.chartInstance && this.chartInstance.getDom && this.chartInstance.getDom() !== this.$refs.chartPreview) {
        this.disposeChart()
      }
      if (!this.chartInstance) this.chartInstance = echarts.init(this.$refs.chartPreview)
      this.chartInstance.clear()
      this.chartInstance.setOption(this.previewChartOption, true)
      this.chartInstance.resize()
    },
    resizeChart() {
      if (this.previewMode === 'echarts' && this.previewViewMode === 'preview') {
        this.$nextTick(() => {
          this.renderChartPreview()
        })
      }
    },
    disposeChart() {
      if (this.chartInstance) {
        this.chartInstance.dispose()
        this.chartInstance = null
      }
    }
  }
}
</script>

<style scoped>
.deepseek-demo-page { height: 100%; padding: 12px; box-sizing: border-box; overflow: hidden; border-radius: 12px; background: #f5f7fb; }
.page-layout, .workspace-body, .result-layout { display: flex; gap: 16px; min-height: 0; }
.page-layout, .workspace-panel, .panel-card, .workspace-body, .answer-section, .text-pane, .preview-pane, .preview-surface, .preview-frame, .chart-preview { height: 100%; }
.page-layout { height: 100%; }
.config-panel { width: 320px; flex: 0 0 320px; min-height: 0; }
.workspace-panel { flex: 1; min-width: 0; min-height: 0; }
.panel-card { border-radius: 18px; border: 1px solid #e7edf5; }
.panel-card ::v-deep .el-card__body { height: calc(100% - 61px); padding: 16px; box-sizing: border-box; overflow: hidden; }
.card-header, .header-left, .header-actions, .section-header, .preview-header, .question-actions, .input-meta, .preview-header-actions { display: flex; align-items: center; gap: 8px; }
.card-header, .section-header, .preview-header, .question-actions, .input-meta { justify-content: space-between; }
.card-body-scroll, .output-panel, .preview-surface { overflow: auto; }
.input-column, .output-column { display: flex; flex-direction: column; min-height: 0; border-radius: 18px; border: 1px solid #d7e2ef; background: linear-gradient(180deg, #fcfdff 0%, #f7fbff 100%); box-shadow: 0 10px 24px rgba(31, 45, 61, 0.05); }
.input-column { flex: 0 0 38%; padding: 18px; }
.output-column { flex: 1; padding: 18px; overflow: hidden; }
.section-title { margin-bottom: 10px; font-size: 16px; font-weight: 600; color: #243447; }
.input-meta { margin-bottom: 14px; color: #7a8797; font-size: 12px; }
.question-editor { flex: 1; min-height: 0; }
.question-textarea ::v-deep .el-textarea, .question-textarea ::v-deep .el-textarea__inner { height: 100%; }
.question-textarea ::v-deep .el-textarea__inner { min-height: 100%; padding: 14px 16px 34px; border-radius: 14px; line-height: 1.8; }
.reasoning-section { display: flex; flex-direction: column; min-height: 0; max-height: 26%; margin-bottom: 14px; }
.answer-section { display: flex; flex-direction: column; flex: 1; min-height: 0; }
.result-layout { flex: 1; }
.result-layout.has-preview .text-pane, .result-layout.has-preview .preview-pane { flex: 1 1 50%; display: flex; flex-direction: column; min-width: 0; min-height: 0; }
.text-pane { flex: 1; min-width: 0; min-height: 0; }
.result-pane { min-height: 0; padding: 12px; border-radius: 18px; border: 1px solid #dbe6f3; background: #ffffff; box-shadow: 0 8px 20px rgba(31, 45, 61, 0.06); }
.preview-title { margin: 0; }
.preview-tag { display: inline-flex; align-items: center; height: 24px; padding: 0 10px; border-radius: 999px; background: #eaf2ff; color: #2f63d8; font-size: 12px; }
.output-panel, .preview-surface { flex: 1; min-height: 0; padding: 16px 18px; border-radius: 16px; background: linear-gradient(180deg, #f8fbff 0%, #eef4fb 100%); color: #243447; line-height: 1.8; word-break: break-word; border: 1px solid #d9e4f1; box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85); }
.output-panel.is-placeholder, .preview-placeholder { color: #8b98a8; }
.preview-surface { padding: 0; overflow: hidden; background: linear-gradient(180deg, #ffffff 0%, #f3f7fc 100%); }
.preview-placeholder { display: flex; align-items: center; justify-content: center; height: 100%; padding: 24px; text-align: center; line-height: 1.8; }
.preview-source-panel { height: 100%; margin: 0; border: none; box-shadow: none; }
.preview-frame { border: 0; background: transparent; }
.config-tip { margin-top: 18px; padding: 14px 16px; border-radius: 14px; background: linear-gradient(180deg, #f7fafc 0%, #eef4fb 100%); color: #5d6b7b; line-height: 1.8; font-size: 13px; }
.config-tip p { margin: 0; }
.markdown-content { white-space: normal; }
.markdown-content ::v-deep p, .markdown-content ::v-deep ul, .markdown-content ::v-deep ol, .markdown-content ::v-deep pre, .markdown-content ::v-deep h1, .markdown-content ::v-deep h2, .markdown-content ::v-deep h3, .markdown-content ::v-deep h4, .markdown-content ::v-deep h5, .markdown-content ::v-deep h6, .markdown-content ::v-deep .code-block { margin: 0 0 14px; }
.markdown-content ::v-deep ul, .markdown-content ::v-deep ol { padding-left: 22px; }
.markdown-content ::v-deep li + li { margin-top: 6px; }
.markdown-content ::v-deep code { padding: 2px 6px; border-radius: 6px; background: #e9eef5; font-family: Consolas, Monaco, monospace; font-size: 13px; }
.markdown-content ::v-deep .code-block { overflow: hidden; border: 1px solid #dbe4ef; border-radius: 14px; background: #1f2937; }
.markdown-content ::v-deep .code-block__label { padding: 8px 14px; background: rgba(255, 255, 255, 0.08); color: #c9d5e6; font-size: 12px; line-height: 1; text-transform: lowercase; }
.markdown-content ::v-deep pre { overflow: auto; margin: 0; padding: 14px 16px; background: #1f2937; }
.markdown-content ::v-deep pre code { display: block; padding: 0; background: transparent; color: #f9fafb; white-space: pre; line-height: 1.7; }
.markdown-content ::v-deep a { color: #2563eb; text-decoration: none; }
.error-message { margin-top: 14px; padding: 12px 14px; border-radius: 12px; color: #d93026; background: #fff4f4; border: 1px solid #ffd8d6; box-shadow: 0 6px 18px rgba(217, 48, 38, 0.08); }
.config-panel-enter-active, .config-panel-leave-active { transition: all 0.25s ease; }
.config-panel-enter, .config-panel-leave-to { opacity: 0; transform: translateX(-16px); }
@media (max-width: 1280px) {
  .deepseek-demo-page { height: 100%; }
  .config-panel { width: 280px; flex-basis: 280px; }
}
@media (max-width: 1200px) {
  .input-column { flex-basis: 42%; }
  .result-layout.has-preview { flex-direction: column; }
}
@media (max-width: 992px) {
  .deepseek-demo-page { height: 100%; overflow: auto; }
  .page-layout, .workspace-body { flex-direction: column; }
  .config-panel { width: 100%; flex-basis: auto; }
  .panel-card { min-height: 320px; }
  .panel-card ::v-deep .el-card__body { height: auto; min-height: 0; }
  .input-column, .output-column { min-height: 280px; }
  .question-editor { min-height: 240px; }
  .reasoning-section { max-height: 220px; }
}
@media (max-width: 768px) {
  .deepseek-demo-page { padding: 10px; }
  .card-header, .header-left, .header-actions, .input-meta, .question-actions, .section-header, .preview-header, .preview-header-actions { flex-wrap: wrap; }
  .header-actions, .question-actions { justify-content: flex-start; }
}
</style>
